import { brandPalette } from "./components/brand-scaffold";

export type NavItem = {
  href?: string;
  label: string;
  children?: { href: string; label: string; description?: string }[];
};

export const siteNavItems: NavItem[] = [
  { href: "/", label: "首页" },
  { href: "/docs", label: "文档" },
  { href: "/marketplace", label: "Marketplace" },
  { href: "/trust-settlement", label: "信任与结算" },
  {
    label: "GitHub",
    children: [
      { href: "https://github.com/hejiajiudeeyu/delegated-execution-client", label: "Use locally", description: "CLI 与本地运行时" },
      { href: "https://github.com/nicekid1/delegated-execution-protocol", label: "Protocol", description: "协议规范" },
      { href: "https://github.com/hejiajiudeeyu/delegated-execution-platform-selfhost", label: "Self-host", description: "自托管平台" },
      { href: "https://github.com/hejiajiudeeyu/delegated-execution-workspace", label: "Workspace", description: "跨仓开发编排" },
    ],
  },
];

export const siteHeaderActions = [
  { href: "/docs/quick-start-responder", label: "5 分钟创建 Hotline", variant: "default" as const },
];

export const navItems = [
  { href: "#brand-system", label: "Brand" },
  { href: "#naming", label: "Naming" },
  { href: "#architecture", label: "Architecture" },
  { href: "#docs", label: "Docs" },
  { href: "#cases", label: "Cases" },
  { href: "#marketplace", label: "Marketplace" },
];

export const heroFacts = [
  {
    label: "Protocol",
    value: "#A3E635",
    note: "Project-wide green anchor",
    color: brandPalette.green,
  },
  {
    label: "Client",
    value: "Blue + roles",
    note: "Caller / Responder split",
    color: brandPalette.blue,
  },
  {
    label: "Platform",
    value: "Purple",
    note: "Official infrastructure layer",
    color: brandPalette.purple,
  },
  {
    label: "Selfhost",
    value: "Yellow",
    note: "Private deployment tier",
    color: brandPalette.yellow,
  },
];

export const brandCards = [
  {
    tone: "protocol" as const,
    kicker: "Protocol",
    title: "Single green contract",
    summary:
      "The protocol uses the project brand green directly. It is not a second green. It is the top-level color anchor for the whole product line.",
    bullets: [
      "One shared green for docs, diagrams, and protocol surfaces.",
      "No independent protocol palette.",
      "Makes the spec layer feel like the project nucleus.",
    ],
  },
  {
    tone: "client" as const,
    kicker: "Client",
    title: "Blue base, role split",
    summary:
      "Client keeps a blue base and uses semantic role colors for Caller and Responder. The identity reads as one product with two behaviors.",
    bullets: [
      "Blue anchors the client family.",
      "Caller defaults to teal.",
      "Responder defaults to orange.",
    ],
  },
  {
    tone: "platform" as const,
    kicker: "Platform",
    title: "Purple infrastructure surface",
    summary:
      "Platform uses purple as its dedicated semantic color. It marks the official infrastructure, routing layer, and public service surface.",
    bullets: [
      "Purple is reserved for platform surfaces.",
      "Used for routing, marketplace, and managed infrastructure cues.",
      "Keeps platform distinct from protocol and selfhost.",
    ],
  },
  {
    tone: "selfhost" as const,
    kicker: "Selfhost",
    title: "Yellow private deployment tier",
    summary:
      "Selfhost uses yellow and represents the private deployment path for enterprise environments and internal network surfaces.",
    bullets: [
      "Private deployment reads as its own tier immediately.",
      "Stays visually separate from the hosted platform.",
      "Works as the enterprise and internal-network path.",
    ],
  },
];

export const namingRows = [
  { source: "Buyer", target: "Caller", note: "The side that initiates the Call." },
  { source: "Seller", target: "Responder", note: "The side that answers the Call." },
  { source: "Service / Line", target: "Hotline", note: "The public-facing service entry." },
  { source: "Call", target: "Call", note: "The verb and noun stay unchanged." },
];

export const callFlowSteps = [
  {
    step: "01",
    title: "Register Hotline",
    description: "Responder publishes the service entry and keeps the core implementation private.",
    tone: "responder" as const,
  },
  {
    step: "02",
    title: "Caller starts the Call",
    description: "The Caller discovers the Hotline and issues the request through the client layer.",
    tone: "caller" as const,
  },
  {
    step: "03",
    title: "Protocol routes the request",
    description: "The protocol enforces the shared contract and keeps the interaction shape stable.",
    tone: "protocol" as const,
  },
  {
    step: "04",
    title: "Result returns and settles",
    description: "The response comes back through the same language, ready for billing, logging, or review.",
    tone: "platform" as const,
  },
];

export const docCards = [
  {
    title: "快速开始",
    summary: "5 分钟完成本地环境搭建，运行第一个 Hotline 任务。",
    accent: brandPalette.green,
    icon: "sparkles" as const,
  },
  {
    title: "协议规范",
    summary: "通信规则、数据格式和交互标准，系统的共享合约层。",
    accent: brandPalette.green,
    icon: "layers" as const,
  },
  {
    title: "Client SDK",
    summary: "Caller / Responder 工作流、本地 CLI 入口和传输层。",
    accent: brandPalette.blue,
    icon: "users" as const,
  },
  {
    title: "自托管部署",
    summary: "私有部署指南，像 GitLab 一样掌控数据和服务。",
    accent: brandPalette.yellow,
    icon: "server" as const,
  },
  {
    title: "Platform API",
    summary: "平台服务接口规范，服务发现、请求路由和中转接口。",
    accent: brandPalette.purple,
    icon: "file" as const,
  },
  {
    title: "Hotline 开发",
    summary: "发布专线、定义输入输出格式、配置适用性说明。",
    accent: brandPalette.pink,
    icon: "globe" as const,
  },
];

export const caseCards = [
  {
    title: "AI 创意内容生成",
    summary: "Caller 提供提示词，Responder 后端多模型管线生成创意图像。生成智能私有运行，接口对外开放。",
    accent: brandPalette.pink,
    points: ["多模型编排和 Prompt 策略不公开。", "风格库和微调模型是核心壁垒。", "适合创意工作室和内容平台。"],
  },
  {
    title: "企业私有知识服务",
    summary: "Caller 发送问题，Responder 私有知识库作答。知识语料和检索策略永远不出本地。",
    accent: brandPalette.blue,
    points: ["私有数据不需要离开本地边界。", "适合企业内部文档问答和合规查询。", "检索策略可持续优化形成护城河。"],
  },
  {
    title: "AI 产品视觉渲染",
    summary: "Caller 上传产品照片，Responder 的微调渲染模型生成电商级视觉素材。模型权重完全私有。",
    accent: brandPalette.purple,
    points: ["微调渲染模型和后处理管线不公开。", "训练成本可转化为商业壁垒。", "适合电商平台和品牌客户批量生产。"],
  },
  {
    title: "专有运行环境",
    summary: "罕见依赖配置和特殊执行环境可以封装为 Hotline，按需调用。",
    accent: brandPalette.teal,
    points: ["环境搭建经验可复用和变现。", "内部工具链可以对外服务化。", "适合需要特定硬件或环境的任务。"],
  },
];

export const marketplaceCards = [
  {
    title: "浏览专线",
    summary: "按能力、分类和信任等级筛选 Hotline，找到适合你任务的专线。",
    accent: brandPalette.green,
  },
  {
    title: "能力分类",
    summary: "按领域和执行模式浏览生态：创意生成、知识问答、图像渲染等。",
    accent: brandPalette.blue,
  },
  {
    title: "Responder 主页",
    summary: "查看 Responder 发布的所有专线、评分和适用性说明。",
    accent: brandPalette.purple,
  },
];
